import { GlobalSearchProvider } from "./experience/GlobalSearch";
import { HomePage } from "./pages/HomePage";
import { ClientImportPage } from "./pages/ClientImportPage";
import { InvoiceImportPage } from "./pages/InvoiceImportPage";
import { SettingsPage } from "./pages/SettingsPage";
import { settingsSections } from "./routing/settingsSections";
import { AccountStatusProvider } from "./components/AccountStatus";
import { useSession } from "./auth/session";
import { SubscriptionBillingPage } from "./pages/SubscriptionBillingPage";
import { ReportsPage } from "./pages/ReportsPage";
import { ReportPage } from "./pages/ReportPage";
import { AccountsAgingPage } from "./pages/AccountsAgingPage";
import { DocumentRouteCanvas, RouteCanvas } from "./components/RouteCanvas";
import { ClientsPage } from "./pages/ClientsPage";
import { ClientDetailPage } from "./pages/ClientDetailPage";
import { ClientSetupPage } from "./pages/ClientSetupPage";
import { RegisterPage } from "./experience/RegisterPage";
import { RegisterShell } from "./experience/RegisterShell";
import { RegisterLayout } from "./experience/RegisterPrimitives";
import { InvoiceDraftPage } from "./pages/InvoiceDraftPage";
import { InvoiceDetailPage } from "./pages/InvoiceDetailPage";
import { ReleaseNotesPage } from "./pages/ReleaseNotesPage";
import { PaymentsPage } from "./pages/PaymentsPage";
import { useLocationUrl } from "./routing/useLocationPath";
import { AppearanceProvider } from "./experience/AppearanceProvider";
import { AuthRoutes } from "./auth/AuthRoutes";
import { AuthShell } from "./auth/AuthShell";
import { AccountStatus } from "./components/AccountStatus";
import { LegalCompletion } from "./legal/LegalAcceptance";

export function App() {
  const {sessionId,state}=useSession();
  return <AppearanceProvider><AccountStatusProvider key={sessionId ?? state} gateRoutes={state === "signed-in"}
    connectingView={connectionState => <AuthShell title={connectionState === "authentication-error"
      ? "Authentication failed" : connectionState === "bootstrap-error" ? "Account connection failed" : "Connecting to SignalBill"}>
      <AccountStatus compact />
    </AuthShell>}>
    <AuthRoutes><LegalCompletion><GlobalSearchProvider><AppRoutes/></GlobalSearchProvider></LegalCompletion></AuthRoutes>
  </AccountStatusProvider></AppearanceProvider>;
}
function AppRoutes() {
  const url = useLocationUrl();
  const path = url.split(/[?#]/,1)[0] ?? "/";
  const document = /^\/(invoices|estimates)\/(new|[0-9a-f-]+)(\/edit)?$/i.test(path);
  const collection = ["/","/clients","/invoices","/estimates","/payments","/reports"].includes(path)
    || /^\/clients\/[0-9a-f-]+$/i.test(path)
    || ["/reports/invoice-details","/reports/payments-collected","/reports/tax-summary","/reports/revenue-by-client","/reports/accounts-aging"].includes(path);
  return <DocumentRouteCanvas routeKey={document ? url : path} locationUrl={url} defer={document || collection} fallback={document ? <RegisterPage kind={path.startsWith("/estimates") ? "estimate" : "invoice"}/> : undefined}><ResolvedRoute path={path}/></DocumentRouteCanvas>;
}
function ResolvedRoute({path}: {path:string}) {
  if (path === "/") return <HomePage/>;
  const clientMatch = path.match(/^\/clients\/([0-9a-f-]+)$/i);
  const clientEditMatch = path.match(/^\/clients\/([0-9a-f-]+)\/edit$/i);
  const estimateMatch=path.match(/^\/estimates\/([0-9a-f-]+)$/i);
  const estimateEditMatch=path.match(/^\/estimates\/([0-9a-f-]+)\/edit$/i);
  const invoiceMatch = path.match(/^\/invoices\/([0-9a-f-]+)$/i);
  const invoiceEditMatch = path.match(/^\/invoices\/([0-9a-f-]+)\/edit$/i);

  if (path === "/settings" || settingsSections.some(section => section.path === path)) return <SettingsPage path={path}/>;

  if (path === "/settings/billing") return <RegisterShell kind="billing"><RegisterLayout><SubscriptionBillingPage /></RegisterLayout></RegisterShell>;

  if (path === "/clients/import") return <ClientImportPage />;

  if (path === "/clients/new") {
    return <ClientSetupPage mode="create" />;
  }

  if (clientEditMatch) {
    return <ClientSetupPage mode="edit" clientId={clientEditMatch[1] ?? ""} />;
  }

  if(path === "/estimates/new") return <InvoiceDraftPage key={path + window.location.search} kind="estimate"/>;
  if(estimateEditMatch) return <InvoiceDraftPage key={path} kind="estimate" invoiceId={estimateEditMatch[1]??""}/>;
  if (path === "/invoices/import") return <InvoiceImportPage />;
  if (path === "/invoices/new") return <InvoiceDraftPage key={path + window.location.search} />;
  if (invoiceEditMatch) return <InvoiceDraftPage key={path} invoiceId={invoiceEditMatch[1] ?? ""} />;
  if (path === "/invoices") return <RegisterPage kind="invoice" />;
  if (path === "/estimates") return <RegisterPage kind="estimate" />;
  if (invoiceMatch) return <RegisterShell kind="invoice"><InvoiceDetailPage invoiceId={invoiceMatch[1] ?? ""}/></RegisterShell>;
  if (estimateMatch) return <RegisterShell kind="estimate"><InvoiceDetailPage kind="estimate" invoiceId={estimateMatch[1] ?? ""}/></RegisterShell>;
  if (path === "/reports/invoice-details" || path === "/reports/payments-collected" || path === "/reports/tax-summary" || path === "/reports/revenue-by-client") {
    return <RegisterShell kind="reports"><RegisterLayout><ReportPage key={path} kind={path === "/reports/invoice-details" ? "invoice-details" : path === "/reports/tax-summary" ? "tax-summary" : path === "/reports/revenue-by-client" ? "revenue-by-client" : "payments-collected"} /></RegisterLayout></RegisterShell>;
  }
  if (path === "/reports/accounts-aging") {
    return <RegisterShell kind="reports"><RegisterLayout><AccountsAgingPage /></RegisterLayout></RegisterShell>;
  }
  if (path === "/clients" || path === "/payments" || path === "/reports") {
    return <RegisterShell kind={path === "/clients" ? "clients" : path === "/payments" ? "payments" : "reports"}><RegisterLayout>
      {path === "/clients" ? <ClientsPage /> : path === "/payments" ? <PaymentsPage /> : <ReportsPage />}
    </RegisterLayout></RegisterShell>;
  }

  return (
    <RegisterShell kind={clientMatch ? "clients" : "utility"}>
      <RouteCanvas key={path} routeKey={path}>
        <RegisterLayout>
          {path === "/release-notes" ? <ReleaseNotesPage /> : clientMatch ? <ClientDetailPage clientId={clientMatch[1] ?? ""} /> : <section className="clients-page"><h1>Page not found</h1><a href="/invoices">Return to invoices</a></section>}
        </RegisterLayout>
      </RouteCanvas>
    </RegisterShell>
  );
}
